import { WarningIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { EhrName } from "@metriport/shared-internal";
import { PatientMapping } from "../../../api/ehr";
import ConfirmDialog from "../../shared/ConfirmDialog";
import { useLinkPatientMutation } from "../../shared/use-patient";

type EhrLinkSectionProps = {
  ehr: EhrName;
  patientMapping: PatientMapping | undefined;
  cxId: string;
};

export default function EhrLinkSection({ ehr, patientMapping, cxId }: EhrLinkSectionProps) {
  const linkPatientMutation = useLinkPatientMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const patientId = patientMapping?.externalId;
  const practiceId = patientMapping?.secondaryMappings?.practiceId;
  const isWrongCustomer = Boolean(cxId && patientMapping && patientMapping.cxId !== cxId);
  const isDisabled = !cxId || !patientId || !practiceId || isWrongCustomer;

  if (isWrongCustomer) {
    return (
      <Box w="full" maxW="4xl">
        <Text fontWeight="bold" mb={2} fontSize="lg">
          Patient does not belong to this customer
        </Text>
      </Box>
    );
  }

  async function handleConfirmLink() {
    if (!cxId || !patientId || !practiceId) return;
    try {
      await linkPatientMutation.mutateAsync({ cxId, patientId, practiceId, ehr });
    } catch (error) {
      // handled in useLinkPatientMutation
    }
    onClose();
  }

  return (
    <Box w="full" maxW="4xl">
      <Text fontWeight="bold" mb={2} fontSize="lg">
        Link {ehr} Patient
      </Text>
      <Text color="gray.600" mb={4} fontSize="sm">
        Link the patient found above in {ehr} app.
      </Text>
      <Button colorScheme="blue" onClick={onOpen} isDisabled={isDisabled}>
        Link Patient
      </Button>
      <ConfirmDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmLink}
        isLoading={linkPatientMutation.isPending}
        title={`Link ${ehr} Patient`}
        confirmText="Link"
        message={
          <VStack align="start" spacing={3}>
            <Flex align="center" gap={2} color="orange.500">
              <WarningIcon />
              <Text fontSize="sm">After linking, ask CX to refresh the page.</Text>
            </Flex>
            <Text>Are you sure you want to link this patient?</Text>
            <Box bg="gray.50" p={3} borderRadius="md" w="full">
              <DetailRow label="Customer ID" value={cxId} />
              <DetailRow label="Patient ID" value={patientId} />
              <DetailRow label="Practice ID" value={practiceId} />
            </Box>
          </VStack>
        }
      />
    </Box>
  );
}

function DetailRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <Text fontSize="sm">
      <Text as="span" fontWeight="semibold" color="gray.600">
        {label}:{" "}
      </Text>
      <Text as="span" fontFamily="mono">
        {value ?? "—"}
      </Text>
    </Text>
  );
}
